<template>
  <section class="btn-product-page">
    <header class="btn-product-page__header">
      <BTNLogo />
    </header>
    <article class="btn-product-page__content">
      <div class="btn-product-page__content-media">
        <div
          v-if="isUnpublished || !webinar.vimeo_id"
          class="btn-product-page__content-media-image-container"
        >
          <img
            :src="getWebinarThumbnail(webinar.thumbnail)"
            class="btn-product-page__content-media-image"
            alt="Webinar video thumbnail image"
          />
        </div>
        <div
          v-else-if="webinar.vimeo_id"
          class="btn-product-page__content-media-video"
        >
          <iframe
            :src="getVimeoUrl()"
            frameborder="0"
            webkitAllowFullScreen
            mozallowfullscreen
            allowfullscreen
            class="btn-product-page__content-media-video-iframe"
          />
        </div>
        <h3 v-else class="btn-product-page__content-media-text">
          No video currently exists for {{ webinar.title }}
        </h3>
      </div>
      <div class="btn-product-page__content-meta">
        <h1 class="btn-product-page__content-meta-title">
          {{ webinar.name }}
        </h1>
        <p class="btn-product-page__content-meta-date">
          {{ getFormattedDate(webinar.date) }}
        </p>
        <p class="btn-product-page__content-meta-description">
          {{ webinar.short_description }}
        </p>
        <BaseLink
          v-if="webinar.ppt_attachment"
          :href="formattedAttachmentLink(webinar.ppt_attachment)"
          :download="webinar.ppt_attachment"
          class="btn-product-page__content-meta-button"
        >
          DOWNLOAD SLIDES
        </BaseLink>
      </div>
    </article>
    <aside class="btn-product-page__question">
      <div class="btn-product-page__question-container">
        <h2 class="btn-product-page__question-title">
          Have questions about a headline you saw recently?
        </h2>
        <p class="btn-product-page__question-text">
          We would love to hear from you! Submit headlines you would like us to
          research and discuss in future episodes. (Please note: Due to ITR
          Economics policy, there may be certain headlines we will not address.
          In such cases, we will reach out to let you know.)
        </p>
      </div>
      <BaseButton
        class="btn-product-page__question-button"
        @click="isAskingQuestion = true"
      >
        SUBMIT A HEADLINE
      </BaseButton>
    </aside>
    <footer
      v-if="otherWebinars.length > 0"
      class="btn-product-page__watch-more"
    >
      <h2 class="btn-product-page__watch-more-title">Watch More...</h2>
      <CarouselContainer class="btn-product-page__watch-more-carousel">
        <CarouselCard
          v-for="otherWebinar in otherWebinars"
          :key="otherWebinar.id"
          class="btn-product-page__watch-more-card"
        >
          <div
            class="btn-product-page__watch-more-card-image-container"
            @click="goToWebinar(otherWebinar.slug)"
            @mouseover="hoveringCards.push(otherWebinar.id)"
            @mouseleave="
              hoveringCards = hoveringCards.filter(
                (cardId) => cardId !== otherWebinar.id
              )
            "
          >
            <img
              :src="getWebinarThumbnail(otherWebinar.thumbnail)"
              class="btn-product-page__watch-more-card-image"
              alt="Webinar video thumbnail image"
            />
            <div
              class="btn-product-page__watch-more-card-overlay"
              :class="{
                'btn-product-page__watch-more-card-overlay--active':
                  hoveringCards.includes(otherWebinar.id),
              }"
            >
              <img
                src="/images/play-icon.png"
                alt="Play Button"
                class="btn-product-page__watch-more-card-overlay-icon"
              />
            </div>
          </div>
          <h4
            class="btn-product-page__watch-more-card-title"
            @click="goToWebinar(otherWebinar.slug)"
          >
            {{ otherWebinar.name }}
          </h4>
          <p
            class="btn-product-page__watch-more-card-date"
            @click="goToWebinar(otherWebinar.slug)"
          >
            {{ getFormattedDate(otherWebinar.date) }}
          </p>
        </CarouselCard>
      </CarouselContainer>
    </footer>
    <SubmitAQuestionModal
      v-if="isAskingQuestion"
      :webinar="webinar"
      :auth-user="authUser"
      @close="handleCloseQuestionModal"
    />
    <SiteNotifications ref="site-notifications" />
  </section>
</template>

<script>
  import CarouselContainer from '~/components/carousel/CarouselContainer.vue';
  import CarouselCard from '~/components/carousel/CarouselCard.vue';
  import BaseButton from '~/components/base/BaseButton.vue';
  import BaseLink from '~/components/base/BaseLink.vue';
  import BTNLogo from '~/components/products/by-the-numbers/Logo.vue';
  import SubmitAQuestionModal from '~/components/products/by-the-numbers/SubmitAQuestionModal.vue';
  import SiteNotifications from '~/components/site/SiteNotifications.vue';

  export default {
    name: 'btn-product-page',
    components: {
      CarouselContainer,
      CarouselCard,
      BaseButton,
      BaseLink,
      BTNLogo,
      SubmitAQuestionModal,
      SiteNotifications,
    },
    props: {
      authUser: {
        type: Object,
        required: true,
      },
      webinar: {
        type: Object,
        required: true,
      },
      otherWebinars: {
        type: Array,
        required: true,
      },
    },
    data: () => ({
      hoveringCards: [],
      isAskingQuestion: false,
    }),
    computed: {
      isUnpublished() {
        const today = new Date();
        const webinarDate = new Date(this.webinar.date);

        today.setHours(0, 0, 0, 0);
        webinarDate.setHours(0, 0, 0, 0);

        return webinarDate > today;
      },
    },
    methods: {
      getVimeoUrl() {
        return `https://player.vimeo.com/video/${this.webinar.vimeo_id}`;
      },
      getWebinarThumbnail(thumbnail) {
        if (!thumbnail) {
          return '/storage/default-thumbnail.jpg';
        }

        let finalThumbnail = '/storage/';

        if (thumbnail.charAt(0) === '/') {
          finalThumbnail += thumbnail.substring(1);
        } else {
          finalThumbnail += thumbnail;
        }

        return finalThumbnail;
      },
      getFormattedDate(date) {
        return new Date(date).toLocaleDateString('en-US', {
          month: 'long',
          year: 'numeric',
        });
      },
      goToWebinar(slug) {
        window.location.href = `/by-the-numbers/${slug}`;
      },
      handleCloseQuestionModal(closeData = {}) {
        this.isAskingQuestion = false;

        if (closeData.sentSuccessfully) {
          this.$refs['site-notifications'].addNotification({
            message: 'Question submitted!',
            color: 'success',
          });
        }
      },
      formattedAttachmentLink(attachment) {
        let attachmentLink =
          attachment.charAt(0) === '/' ? attachment.substring(1) : attachment;
        return `/storage/${attachmentLink}`;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .btn-product-page {
    display: flex;
    flex-direction: column;
    gap: 64px;
    padding: 64px;
    min-height: 85vh;

    &__header {
      width: 100%;
    }

    &__content {
      flex: 1 auto;
      display: flex;
      flex-direction: column;
      gap: 32px;

      @include screen-xl {
        flex-direction: row;
      }

      &-media {
        flex: 1 auto;

        &-image-container {
          position: relative;
          width: 100%;
          height: 0;
          padding-bottom: 56.25%;
          z-index: 0;

          &::before {
            content: '';
            position: absolute;
            width: 100%;
            height: 100%;
            background: linear-gradient(180deg, #00a785, #3075a4);
            z-index: 1;
          }
        }
        &-image {
          position: absolute;
          width: 100%;
          height: 100%;
          object-fit: cover;
          z-index: 2;
        }
        &-video {
          position: relative;
          padding-bottom: 56.25%;
          height: 0;
          overflow: hidden;
          max-width: 100%;
          height: auto;

          &-iframe {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
          }
        }
        &-text {
          text-align: center;
        }
        &--text {
          color: var(--itr-charcoal);
          padding: 0;
          margin: 0;
        }
      }

      &-meta {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        width: 100%;

        &-title {
          color: var(--itr-blue-mid);
          font-size: 30px;
          font-weight: 800;
          width: 100%;
          text-align: left;
          margin: 0;
          line-height: 40px;
        }
        &-date {
          color: var(--itr-grey-mid);
          text-align: left;
          font-size: 18px;
          font-weight: 800;
          padding: 32px 0;
        }
        &-description {
          color: var(--itr-charcoal);
          text-align: left;
          font-weight: 400;
          line-height: 26px;
          margin: 0;
          font-size: 16px;
        }
        &-button {
          padding: 4px 32px;
          overflow: inherit;
          margin: 32px 0;
        }

        @include screen-xl {
          max-width: 310px;
          align-items: flex-end;

          &-title {
            text-align: right;
          }
          &-date {
            text-align: right;
          }
          &-description {
            text-align: right;
            max-width: 297px;
          }
        }
      }
    }

    &__question {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      width: 100%;
      gap: 32px;

      &-container {
        flex: 1 auto;
        display: flex;
        flex-direction: column;
      }
      &-title {
        font-size: 35px;
        font-weight: 700;
        margin: 0;
        padding: 0;
        margin-bottom: 16px;
      }
      &-text {
        color: var(--itr-charcoal);
        font-size: 16px;
        font-weight: 400;
        line-height: 26px;
        margin: 0;
      }
      &-button {
        padding: 8px 42px;
        overflow: inherit;
      }

      @include screen-md {
        align-items: flex-start;
        justify-content: flex-start;
      }

      @include screen-xl {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        width: fit-content;
      }
    }

    &__watch-more {
      &-title {
        font-size: 35px;
        font-weight: 700;
        margin: 0;
        padding: 0;
        margin-bottom: 32px;
      }

      &-carousel {
        width: 100%;
        display: flex;
        gap: 32px;
        overflow-x: auto;
        padding-bottom: 16px;
        scroll-snap-type: x mandatory;
        -webkit-overflow-scrolling: touch;
        -ms-overflow-style: -ms-autohiding-scrollbar;
      }

      &-card {
        height: 100%;
        min-height: 300px;
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 0;
        margin: 1rem;
        overflow: hidden;

        width: 222px;
        @media (min-width: 768px) {
          width: 276px;
        }
        @media (min-width: 1200px) {
          width: 399px;
        }

        &-image-container {
          position: relative;
          width: 100%;
          cursor: pointer;
          z-index: 0;
          background: linear-gradient(180deg, #00a785, #3075a4);

          height: 147px;

          @media (min-width: 768px) {
            height: 183px;
          }
          @media (min-width: 1200px) {
            height: 266px;
          }
        }
        &-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          cursor: pointer;
          z-index: 2;
        }
        &-overlay {
          display: flex;
          justify-content: center;
          align-items: center;
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          cursor: pointer;
          z-index: -1;
          opacity: 0;

          transition:
            opacity 300ms ease-in-out,
            z-index 0ms 300ms ease-in-out;

          &--active {
            opacity: 1;
            z-index: 3;

            transition: opacity 300ms ease-in-out;
          }

          &-icon {
            cursor: pointer;
          }
        }
        &-title {
          flex: 1 auto;
          font-size: 20px;
          font-weight: 800;
          font-size: 18px;
          margin: 0;
          padding: 0;
          color: var(--itr-blue-mid);
          line-height: 24px;
          cursor: pointer;
        }
        &-date {
          color: var(--itr-charcoal);
          font-weight: 400;
          font-size: 14px;
          padding: 8px 0;
          line-height: 23px;
          cursor: pointer;
        }
      }
    }
  }
</style>
